<template>
  <div>
    <breadcrumbs home="Product"/>

    <v-card flat>
      <v-tabs v-model="tab" class="full-height-tab" show-arrows>
        <v-tab>Info</v-tab>
        <v-tab>Building</v-tab>
        <v-tab v-if="productId==undefined">Initial</v-tab>
        <v-tab v-if="product.alcoholType!=='NonExcise'">In-bond Selling</v-tab>
        <v-tab>Duty-paid Selling</v-tab>
        <v-tab>Purchasing</v-tab>

        <v-tab-item eager>
          <inventory-products-edit-info :product="product" :accounts="inventoryAccounts" :spirits="spirits" :nonExcises="nonExcises" :wines="wines" :values="values" ref="info"/>
        </v-tab-item>
        <v-tab-item eager>
          <inventory-products-edit-building :product="product" ref="building"/>
        </v-tab-item>
        <v-tab-item v-if="productId==undefined">
          <inventory-products-edit-init :product="product" ref="init"/>
        </v-tab-item>
        <v-tab-item v-if="product.alcoholType!=='NonExcise'">
          <inventory-products-edit-bonded-selling :product="product" :taxes="taxes.filter(tax => tax.salesRate !== null)" :incomeAccounts="incomeAccounts" :cogAccounts="cogAccounts" :values="values" ref="bondedselling"/>
        </v-tab-item>
        <v-tab-item>
          <inventory-products-edit-duty-paid-selling :product="product" :taxes="taxes.filter(tax => tax.salesRate !== null)" :incomeAccounts="incomeAccounts" :cogAccounts="cogAccounts" :values="values" ref="dutypaidselling"/>
        </v-tab-item>
        <v-tab-item eager>
          <inventory-products-edit-buying :product="product" :taxes="taxes.filter(tax => tax.purchaseRate !== null)" ref="buying"/>
        </v-tab-item>
      </v-tabs>

      <v-divider/>
      <v-card-actions class="pr-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

import InventoryProductsEditInfo from '@/views/inventory/products/InventoryProductsEditInfo'
import InventoryProductsEditBuilding from '@/views/inventory/products/InventoryProductsEditBuilding'
import InventoryProductsEditInit from '@/views/inventory/products/InventoryProductsEditInit'
import InventoryProductsEditBondedSelling from '@/views/inventory/products/InventoryProductsEditBondedSelling'
import InventoryProductsEditDutyPaidSelling from '@/views/inventory/products/InventoryProductsEditDutyPaidSelling'
import InventoryProductsEditBuying from '@/views/inventory/products/InventoryProductsEditBuying'

export default {
  name: 'InventoryProductsEdit',
  mixins: [confirmLeave],
  components: {InventoryProductsEditInfo,InventoryProductsEditBuilding,InventoryProductsEditInit,
               InventoryProductsEditBondedSelling,InventoryProductsEditDutyPaidSelling,InventoryProductsEditBuying},
  props: {
    productId: {type:String, required:false}
  },
   data () {
      return {
        accounts: [],
        taxes: [],
        spirits: [],
        nonExcises: [],
        wines: [],
        product: {provincialDetails:{},productionPlanningInfo:{equipmentTypes:[]},vendors:[],bondedCustomers:[],customers:[],bom:[],pack:[]},
        dirty: false,
        saving: false,
        loading: false,
        tab: 0
      }
    },
  computed: {
    connectedToAccounting() {
      return this.$store.getters.company.connectedToAccounting;
    },
    inventoryAccounts() {
      return this.accounts.filter(account => account.type === 'OtherCurrentAsset');
    },
    incomeAccounts() {
      return this.accounts.filter(account => account.type === 'Income');
    },
    cogAccounts() {
      return this.accounts.filter(account => account.type === 'CostOfGoodsSold');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    }
  },
  watch: {
    product: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.loading = true;
    this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.productId ? [] : [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.productId ? [] : [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.productId ? [] : [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,purchaseRate', 100);
    const results = await this.$apiCall(`query InventoryProductsEdit($accounting:Boolean!, $currency:String!, $id:ID!, $edit:Boolean!) {
                                              accounts(currency:$currency) @include(if:$accounting) {
                                                ref,name,type
                                              }
                                              companyAccounts @skip(if:$edit) {
                                                bondedProductInventory,bondedProductCOG,bondedProductIncome,
                                                dutyPaidProductInventory,dutyPaidProductCOG,dutyPaidProductIncome
                                              }
                                              product(id:$id) @include(if:$edit) {
                                                id,name,sku,active,category{id},description,existingTransactions,
                                                mixedPack,
                                                alcohol{type,id},strength,allowVariableStrength,bottleSize,containerType,caseSize,bottlesPerUnit,caseWeight,palletTI,palletHI,
                                                upc,scc,bom{type,id,quantity,per,optional}
                                                bondedInventoryAccountRef,bondedIncomeAccountRef,bondedCogAccountRef,
                                                dutyPaidInventoryAccountRef,dutyPaidIncomeAccountRef,dutyPaidCogAccountRef,
                                                pictureURL,provincialDetails,
                                                lowBondedInventoryCases,lowDutyPaidInventoryBottles,
                                                sellingBondedDescription,defaultBondedPrice,defaultBondedTax{id},
                                                sellingDescription,defaultPrice,defaultTax{id},
                                                buyingDescription,preferredVendor{id},
                                                dutyPaidCommissionExempt,inBondCommissionExempt,
                                                productionPlanningInfo {useAverageDuration,specifiedDuration,equipmentTypes{id,quantity},roleId}
                                                vendors{vendorId,tax{id},sku,cost},
                                                bondedCustomers{category,customerId,tax{id},sku,price},
                                                customers{category,customerId,tax{id},sku,price},
                                                pack{id,quantity}
                                              }
                                            }`,
                                        {accounting:this.connectedToAccounting,
                                          currency:this.$store.getters.company.settings.homeCurrency,
                                          edit:this.productId!=undefined,id:this.productId||''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.connectedToAccounting)
      this.accounts = results.data.accounts;

    if (this.productId) {
      this.product = {...results.data.product,
                      alcoholType:results.data.product.alcohol.type,alcoholId:results.data.product.alcohol.id,alcohol:undefined,
                      preferredVendorId:results.data.product.preferredVendor?.id,preferredVendor:undefined,
                      defaultTaxId:results.data.product.defaultTax?.id,defaultTax:undefined,
                      defaultBondedTaxId:results.data.product.defaultBondedTax?.id,defaultBondedTax:undefined,
                      categoryId:results.data.product.category?.id,category:undefined,
                      vendors:results.data.product.vendors.map(v => ({...v,taxId:v.tax.id,tax:undefined})),
                      bondedCustomers:results.data.product.bondedCustomers.map(c => ({...c,taxId:c.tax.id,tax:undefined})),
                      customers:results.data.product.customers.map(c => ({...c,taxId:c.tax.id,tax:undefined}))
                     };
      let key = Date.now();
      this.product.bom.forEach(item => item.key = key++);
      this.product.pack.forEach(product => product.key = key++);
      this.product.provincialDetails = this.product.provincialDetails ? JSON.parse(this.product.provincialDetails) : {};
    }

    else {
      if (this.connectedToAccounting && this.inventoryAccounts.length === 0) {
        this.$store.commit('setError', {message:'A product cannot be created without an Other Current Asset account defined',type:'error'})
        this.$nextTick(() => {
          this.dirty = false;
          this.$router.back();
        });
        return;
      }

      this.product = {name:'',sku:'',active:true,alcoholType:'Spirit',categoryId:null,description:'',
                      mixedPack:false, pack:[{id:'',quantity:1}],
                      bondedInventoryAccountRef:results.data.companyAccounts.bondedProductInventory,
                      bondedIncomeAccountRef:results.data.companyAccounts.bondedProductIncome,
                      bondedCogAccountRef:results.data.companyAccounts.bondedProductCOG,
                      dutyPaidInventoryAccountRef:results.data.companyAccounts.dutyPaidProductInventory,
                      dutyPaidIncomeAccountRef:results.data.companyAccounts.dutyPaidProductIncome,
                      dutyPaidCogAccountRef:results.data.companyAccounts.dutyPaidProductCOG,
                      alcoholId:'',strength:undefined,allowVariableStrength:false,bottleSize:undefined,containerType:'Glass',bottlesPerUnit:1,
                      caseSize:undefined,caseWeight:10,palletTI:10,palletHI:4,upc:'',scc:'',
                      bom:[],lowBondedInventoryCases:-1,lowDutyPaidInventoryBottles:-1,
                      sellingBondedDescription:'',defaultBondedPrice:0,defaultBondedTaxId:null,bondedCustomers:[],
                      sellingDescription:'',defaultPrice:0,defaultTaxId:null,customers:[],
                      buyingDescription:'',preferredVendorId:null,vendors:[],provincialDetails:{},
                      productionPlanningInfo:{useAverageDuration:true,specifiedDuration:0,equipmentTypes:[],roleId:''},
                      initial:[],initialAsof:format(new Date(), 'yyyy-MM-dd HH:mm:ss')};
    }
    this.$nextTick(() => this.dirty = false);
    this.loading = false;
  },

  methods: {
    cancel() {
      this.$router.back();
    },
    async save() {
      let valid = true;
      if (!this.$refs.info.valid) {
        this.tab = 0;
        valid = false;
      }
      else if (this.$refs.building && !this.$refs.building.valid) {
        this.tab = 1;
        valid = false;
      }
      else if (!this.productId && this.$refs.init && !this.$refs.init.valid) {
        this.tab = 2;
        valid = false;
      }
      const tabOffset = !this.productId ? 2 : 1;
      if (valid && this.$refs.bondedselling && !this.$refs.bondedselling.valid) {
        this.tab = 1 + tabOffset;
        valid = false;
      }
      else if (this.$refs.dutypaidselling && (!this.$refs.dutypaidselling.valid || !this.$refs.dutypaidselling.provinceValid)) {
        this.tab = 2 + tabOffset;
        valid = false;
      }
      else if (this.$refs.buying && !this.$refs.buying.valid) {
        this.tab = 3 + tabOffset;
        valid = false;
      }

      if (!valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {pictureURL,alcoholType,existingTransactions, ...input} = {...this.product, provincialDetails:JSON.stringify(this.product.provincialDetails)};
      if (!this.product.id)
        input.initial = input.initial.map(init => {     // Remove id field from initial list
          const {id,...line} = init;
          return line;
        });
      input.bom = input.bom.map(item => {
        const {key, ...strippedItem} = item;
        return strippedItem;
      });
      input.pack = input.mixedPack ? input.pack.map(product => {
        const {key, ...strippedProduct} = product;
        return strippedProduct;
      }) : null;
      if (input.mixedPack) {    // Just to pass validation
        input.bottleSize = 1;
      }
      const results = await this.$apiCall(`mutation InventoryProductsEdit($input: ProductInput!) {
                                             productCreate(input:$input) {id,name,category{id},active,scc,upc}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.$store.commit(this.product.id ? 'productUpdate' : 'productAdd', results.data.productCreate);
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.product.id?'Updated':'Created'} product ${this.product.name}`});
      }
    }
  }
}
</script>